import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "@/config/axiosInstance";
import { Button } from "@/components/ui/button";
import { endpoints } from "@/config/apiEndpoints";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  Mail,
  Target,
  Users,
  BarChart3,
  Zap,
  Shield,
  Check,
  X,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import heroProperties from "@/assets/hero-properties.jpg";
import mainlogo from "@/assets/mylistiq-logo-new.png";
import { useToast } from "@/components/ui/use-toast";
import CallToActionFooter from "@/components/Footer";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
// import Chatbot from "@/components/Chatbot";
// import CallingBot from "@/components/Callingbot";
import BotLauncher from "@/components/BotLauncher";
import Unsubscribe from "@/components/Unsubscribe";

// FIXED PLAN ORDER
const PLAN_ORDER = ["one-time", "starter", "pro", "pro+"];

const getPlanSortIndex = (planName: string): number => {
  const baseName = planName
    .replace(/\s*(Annual(?:\s+Plan)?|Monthly|One[-_ ]?Time)/gi, "")
    .trim()
    .toLowerCase();
  const index = PLAN_ORDER.findIndex((order) =>
    baseName.includes(order.toLowerCase())
  );
  return index >= 0 ? index : PLAN_ORDER.length;
};

const normalizePlansResponse = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
};

// ── AUTO IQ Overlay ──────────────────────────────────────────────────────────
const AutoIQOverlay = ({
  isAnimating,
  label,
}: {
  isAnimating: boolean;
  label: string;
}) => (
  <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
    <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-2xl shadow-2xl border-2 border-white/20 backdrop-blur-sm animate-in fade-in zoom-in duration-500">
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 bg-green-400"></span>
        </span>
        <span className="text-lg sm:text-2xl font-bold tracking-wider">AUTO IQ</span>
        {isAnimating && (
          <svg
            className="w-5 h-5 sm:w-8 sm:h-8 animate-spin"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        )}
      </div>
      <p className="text-center text-xs sm:text-sm mt-1.5 sm:mt-2 opacity-90 font-medium">
        {isAnimating ? `Enhancing ${label}...` : `Ready to Enhance ${label}`}
      </p>
    </div>
  </div>
);

// ── Sparkle Effects ───────────────────────────────────────────────────────────
const SparkleEffects = () => (
  <>
    <div className="absolute top-1/4 left-1/4 animate-ping z-20">
      <svg className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0l3.09 6.26L22 7.27l-5 4.87 1.18 6.88L12 15.77l-6.18 3.25L7 12.14 2 7.27l6.91-1.01L12 0z" />
      </svg>
    </div>
    <div className="absolute top-3/4 right-1/4 animate-ping animation-delay-200 z-20">
      <svg className="w-5 h-5 sm:w-8 sm:h-8 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0l3.09 6.26L22 7.27l-5 4.87 1.18 6.88L12 15.77l-6.18 3.25L7 12.14 2 7.27l6.91-1.01L12 0z" />
      </svg>
    </div>
    <div className="absolute top-1/2 right-1/3 animate-ping animation-delay-400 z-20">
      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0l3.09 6.26L22 7.27l-5 4.87 1.18 6.88L12 15.77l-6.18 3.25L7 12.14 2 7.27l6.91-1.01L12 0z" />
      </svg>
    </div>
    <div className="absolute bottom-1/4 left-1/3 animate-ping animation-delay-600 z-20">
      <svg className="w-4 h-4 sm:w-7 sm:h-7 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0l3.09 6.26L22 7.27l-5 4.87 1.18 6.88L12 15.77l-6.18 3.25L7 12.14 2 7.27l6.91-1.01L12 0z" />
      </svg>
    </div>
  </>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const Home = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [pricingPlans, setPricingPlans] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(false);
  const [trialEnabled, setTrialEnabled] = useState(true);
  // Site-wide trial toggle (Site Settings in admin) folded into the same
  // "already used trial" signal every downstream check already understands —
  // disabling it site-wide behaves exactly like the user having used theirs.
  const effectiveHasUsedFreeTrial = hasUsedFreeTrial || !trialEnabled;

  useEffect(() => {
    axiosInstance
      .get(endpoints.site_settings)
      .then((res) => setTrialEnabled(res.data?.trial_enabled ?? true))
      .catch(() => setTrialEnabled(true));
  }, []);

const [showUnsubscribe, setShowUnsubscribe] = useState(true);
const [unsubEmail, setUnsubEmail]           = useState("");
const [unsubDone, setUnsubDone]             = useState(false);

  const isOneTimeName = (name?: string) => /one[-_ ]?time/i.test(name || "");

  const displayBaseName = (name: string) =>
    name.replace(/\s*(Annual(?:\s+Plan)?|Monthly|One[-_ ]?Time)/ig, "").trim();
  useEffect(() => {
    const params   = new URLSearchParams(window.location.search);
    let emailParam = params.get("email") || "";
    const done     = params.get("done") === "1";
    try { emailParam = decodeURIComponent(emailParam); } catch {}
    if (emailParam || done) {
      setUnsubEmail(emailParam);
      setUnsubDone(done);
      setShowUnsubscribe(true);
    }
  }, []);

 useEffect(() => {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes marquee {
      from { transform: translateX(0); }
      to   { transform: translateX(-25%); }
    }
  `;
  document.head.appendChild(style);
  return () => { document.head.removeChild(style); };
}, []);
  const isTrialEligiblePlan = (plan: any, hasUsedFreeTrial: boolean) => {
    const isOneTimePlan =
      Boolean(plan?.is_one_time) ||
      plan?.billing_cycle === "one_time" ||
      isOneTimeName(plan?.name);
    return !isOneTimePlan && !hasUsedFreeTrial;
  };

  const purchasePlan = async (plan: any) => {
    try {
      const res = await axiosInstance.post(endpoints.subscribe, {
        plan_id: plan.id,
        billing_cycle: plan?.billing_cycle || billingCycle,
      });
      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("Checkout URL was not returned");
      }
    } catch (err: any) {
      toast({
        title: "Payment failed",
        description: err.response?.data?.error || err.message || "Please try again",
        variant: "destructive",
      });
    }
  };

  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [trialDialogPlan, setTrialDialogPlan] = useState<any | null>(null);
  const [isTrialDialogLoading, setIsTrialDialogLoading] = useState(false);

  const confirmTrialDialogPurchase = async () => {
    if (!trialDialogPlan) return;
    setIsTrialDialogLoading(true);
    setTrialDialogOpen(false);
    await purchasePlan(trialDialogPlan);
    setIsTrialDialogLoading(false);
    setTrialDialogPlan(null);
  };

  const handlePricingCardHomeClick = async (plan: any) => {
    const token = localStorage.getItem("access_token");
    const hasPlan = localStorage.getItem("has_plan") === "true";

    if (token && hasPlan) {
      toast({ title: "You already have an active plan 🎉", variant: "success", duration: 2000 });
      navigate("/dashboard");
      return;
    }
    if (!token) {
      toast({ title: "Login required", variant: "destructive" });
      navigate("/signup");
      return;
    }
    if (isTrialEligiblePlan(plan, effectiveHasUsedFreeTrial)) {
      setTrialDialogPlan(plan);
      setTrialDialogOpen(true);
      return;
    }
    await purchasePlan(plan);
  };

  const [isDemoVideoOpen, setIsDemoVideoOpen] = useState(false);
  const [showEnhancedContent, setShowEnhancedContent] = useState(false);

  const features = [
    { icon: Mail, title: "Email Marketing", description: "Create stunning property marketing emails that convert leads into clients." },
    { icon: Target, title: "Targeted Launches", description: "Reach the right buyers with precision-targeted marketing Launches." },
    { icon: Users, title: "Smart by AI", description: "Eye-Catching Photos. Click-Worthy Subject Lines. AI-Written Descriptions." },
    { icon: BarChart3, title: "Analytics & Insights", description: "Track Launch performance and optimize your marketing strategy." },
    { icon: Zap, title: "Automation", description: "Automate your marketing workflow and save valuable time." },
    { icon: Shield, title: "Value That's Affordable", description: "Simple Pricing That Grows With You." },
  ];

  // ── Carousel State ───────────────────────────────────────────────────────────
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showAutoAI, setShowAutoAI] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const autoAdvanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const originalSubject = "New Listing Available";
  const enhancedSubject = "🏡 Stunning 4BR Luxury Home Just Listed in Prime Location!";
  const originalDescription = "Nice house for sale. Has 4 bedrooms and 3 bathrooms. Good location.";
  const enhancedDescription =
    "Discover your dream home in this meticulously maintained 4-bedroom, 3-bathroom luxury residence. Nestled in a prime neighborhood with top-rated schools and shopping nearby, this stunning property offers modern elegance and timeless charm.";

  const clearAllTimers = () => {
    if (autoAdvanceTimeoutRef.current) { clearTimeout(autoAdvanceTimeoutRef.current); autoAdvanceTimeoutRef.current = null; }
    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
    if (completionTimeoutRef.current) { clearTimeout(completionTimeoutRef.current); completionTimeoutRef.current = null; }
  };

  useEffect(() => { return () => clearAllTimers(); }, []);

  useEffect(() => {
    if (currentSlide !== 0) return;
    clearAllTimers();
    setShowEnhancedContent(false);
    setIsAnimating(true);
    setShowAutoAI(true);
    const animationDuration = 2000;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        setShowAutoAI(false);
        setShowEnhancedContent(true);
        autoAdvanceTimeoutRef.current = setTimeout(() => {
          if (!isHovered) setCurrentSlide(1);
        }, 3500);
      }
    };
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => clearAllTimers();
  }, [currentSlide, isHovered]);

  useEffect(() => {
    if (currentSlide !== 1) return;
    clearAllTimers();
    setShowEnhancedContent(false);
    setIsAnimating(true);
    setShowAutoAI(true);
    const animationDuration = 2000;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        setShowAutoAI(false);
        setShowEnhancedContent(true);
        autoAdvanceTimeoutRef.current = setTimeout(() => {
          if (!isHovered) setCurrentSlide(0);
        }, 3500);
      }
    };
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => clearAllTimers();
  }, [currentSlide, isHovered]);

  const nextSlide = () => { clearAllTimers(); setCurrentSlide((prev) => (prev + 1) % 2); };
  const prevSlide = () => { clearAllTimers(); setCurrentSlide((prev) => (prev - 1 + 2) % 2); };

  const handleCarouselHover = (hovering: boolean) => {
    setIsHovered(hovering);
    if (hovering && autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axiosInstance.get(endpoints.plans);
        const normalizedPlans = normalizePlansResponse(res.data);
        setPricingPlans(normalizedPlans);
        const token = localStorage.getItem("access_token");
        if (token) {
          try {
            const userRes = await axiosInstance.get(endpoints.me);
            setHasUsedFreeTrial(userRes.data.has_used_free_trial || false);
          } catch (err) {
            console.warn("Failed to fetch user data:", err);
          }
        }
      } catch (err) {
        console.error("Failed to load plans:", err);
        setPricingPlans([]);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const safePricingPlans = Array.isArray(pricingPlans) ? pricingPlans : [];

  // ── Pricing Card ─────────────────────────────────────────────────────────────
  const PricingCardHome = ({
    plan,
    features = [],
    isOneTime = false,
    showAnnualSavings = false,
    annualBenefit = null,
    billingCycle = "monthly",
    allPlans = [],
    hasUsedFreeTrial = false,
  }: any) => {
    const displayFeatures =
      Array.isArray(features) && features.length > 0
        ? features
        : Array.isArray(plan.features)
          ? plan.features
          : [];

    const getMonthlyPlan = () => {
      if (isOneTime) return null;
      const baseName = displayBaseName(plan.name);
      return allPlans.find(
        (p: any) => displayBaseName(p.name) === baseName && p.billing_cycle === "monthly"
      );
    };

    const monthlyPlan = getMonthlyPlan();
    const monthlyPrice = billingCycle === "annual" ? parseFloat(monthlyPlan?.price || 0) : parseFloat(plan.price);
    const annualFullPrice = monthlyPrice * 12;
    const annualActualPrice = billingCycle === "annual" ? parseFloat(plan.price) : 0;
    const annualSavings = annualFullPrice - annualActualPrice;

    const isFreePlan = isOneTime
      ? Number(plan.price) === 0
      : billingCycle === "monthly"
      ? monthlyPrice === 0
      : annualActualPrice === 0;

    return (
      <Card className="relative bg-card border rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
        {showAnnualSavings && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-800 text-white px-4 py-1.5 rounded-full text-sm font-bold z-10 shadow-lg">
            Save 10%
          </div>
        )}
        <CardContent className="p-6 sm:p-8 flex flex-col h-full">
          <div className="text-center mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-4">
              {isOneTime ? "One-Time" : displayBaseName(plan.name)}
            </h3>
            {annualBenefit && (
              <div className="mb-5 bg-gradient-to-r from-purple-800 to-blue-800 text-white text-sm font-semibold py-2.5 px-4 rounded-lg">
                {annualBenefit}
              </div>
            )}
            <div className="mb-4">
              {isOneTime ? (
                Number(plan.price) === 0 ? (
                  <div className="text-3xl sm:text-4xl font-bold text-foreground">Free</div>
                ) : (
                  <div className="text-3xl sm:text-4xl font-bold text-foreground">${plan.price}</div>
                )
              ) : billingCycle === "monthly" ? (
                <div>
                  <span className="text-3xl sm:text-4xl font-bold text-foreground">${monthlyPrice.toFixed(2)}</span>
                  <span className="text-sm text-muted-foreground">/month</span>
                </div>
              ) : billingCycle === "annual" && monthlyPrice > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Annual Billing</p>
                  <p className="text-lg line-through text-muted-foreground font-medium">${annualFullPrice.toFixed(2)}</p>
                  <p className="text-3xl font-bold text-black-600">${annualActualPrice.toFixed(2)}</p>
                  <p className="text-xs text-green-600 font-medium">Save ${annualSavings.toFixed(2)}/year</p>
                </div>
              ) : null}
            </div>
          </div>
          <ul className="space-y-2 flex-1 mb-5 text-sm leading-tight">
            {displayFeatures.map((f: any) => (
              <li key={f.id} className="flex items-start gap-2.5">
                {f.status ? (
                  <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                )}
                <span className={`text-xs sm:text-sm ${f.status ? "text-foreground" : "text-muted-foreground line-through"}`}>
                  {f.name}
                </span>
              </li>
            ))}
          </ul>
          <Button
            variant="cta"
            size="lg"
            className="w-full font-semibold"
            onClick={() => handlePricingCardHomeClick(plan)}
          >
            {(() => {
              const token = localStorage.getItem("access_token");
              const hasPlan = localStorage.getItem("has_plan") === "true";
              const label =
                token && hasPlan
                  ? "Go to Dashboard"
                  : isFreePlan
                  ? "Get Started"
                  : isTrialEligiblePlan(plan, hasUsedFreeTrial)
                  ? "Start 7-Day Free Trial"
                  : `Go ${isOneTime ? "One-Time" : displayBaseName(plan.name)}`;
              return (
                <>
                  {label}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              );
            })()}
          </Button>
        </CardContent>
      </Card>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      {/* {showUnsubscribe && (
  <Unsubscribe
    initialEmail={unsubEmail}
    initialDone={unsubDone}
    onClose={() => setShowUnsubscribe(false)}
  />
)} */}

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-gradient-to-b from-blue-100/90 to-blue-50/90">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">

            {/* Logo */}
            <div className="flex items-center">
              <Link
                to="/"
                onClick={(e) => {
                  if (window.location.pathname === "/") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                className="flex items-center gap-2"
              >
                <img src={mainlogo} alt="MyListIQ" className="h-10 sm:h-12 w-auto drop-shadow-sm" />
              </Link>
            </div>

            <div
  className="flex-1 min-w-0 overflow-hidden"
  style={{ maskImage: "linear-gradient(to right, transparent 0, black 48px, black calc(100% - 48px), transparent 100%)" }}
>
  <div
    className="flex w-max hover:[animation-play-state:paused]"
    style={{ animation: "marquee 20s linear infinite" }}
  >
    {[...Array(4)].map((_, i) => (
      <span key={i} className="whitespace-nowrap text-xs text-red-500 text-muted-foreground px-12">
        {/* To unsubscribe, email{" "}
        <a href="mailto:support@mylistiq.com" className="text-blue-600 font-medium hover:underline">
          support@mylistiq.com
        </a>{" "}
        with subject "UNSUBSCRIBE" and include the email address you wish to remove. */}
        {/* Try it free for 7 Days - no risk, all reward */}
      </span>
    ))}
  </div>
</div>
            {/* Right side: nav links (desktop) + CTA button (always visible) */}
            <div className="flex items-center gap-3 sm:gap-6">
              {/* Desktop nav links only */}
              <div className="hidden md:flex items-center space-x-6">
                <button
                  onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                  className="text-slate-600 hover:text-blue-600 font-medium transition-colors text-sm"
                >
                  Features
                </button>
                <button
                  onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                  className="text-slate-600 hover:text-blue-600 font-medium transition-colors text-sm"
                >
                  Pricing
                </button>
                <Link to="/about" className="text-slate-600 hover:text-blue-600 font-medium transition-colors text-sm">
                  About
                </Link>
              </div>

              {/* CTA button — always visible on all screen sizes */}
              <Button
                asChild
                size="sm"
                className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-xl transition-all px-3 sm:px-4"
              >
                <Link to={localStorage.getItem("access_token") ? "/dashboard" : "/signup"}>
                  {localStorage.getItem("access_token") ? "Dashboard" : "Sign Up"}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero Section ── */}
      <section className="relative py-10 sm:py-16 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950 dark:via-indigo-950 dark:to-purple-950"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-200/20 via-transparent to-transparent dark:from-blue-800/20"></div>

        <div className="relative mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

            {/* Text content — below carousel on mobile, left on desktop */}
            <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
              <div className="space-y-4 sm:space-y-6">
                <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight">
                  <span className="text-foreground">
                    Marketing by
                    <br />
                    Email
                  </span>
                  <br />
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Made Simple
                  </span>
                </h1>
                <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                  {/* Create and launch professional flyers in minutes.{" "}
                  <span className="text-red-600 text-lg sm:text-2xl font-bold mx-1">AI powers</span>{" "}
                  scroll-stopping subject lines, compelling descriptions, and enhanced photos. Instantly
                  schedule distribution through built-in email hubs, your own custom hub, or across social
                  platforms—while intelligent analytics track opens, clicks, optimized send times, and
                  predictive performance so you can measure and scale success. */}

                  Create professional email marketing campaigns in minutes.{" "}
                  <span className="text-sm sm:text-lg font-bold text-red-600">
                    MyListIQ's AI{" "}
                  </span>
                  writes your subject lines, crafts your descriptions,
                   and enhances your photos — so every listing or service you promote looks polished, never rushed. Reach agents across your county 
                   through built-in Hubs, and track opens and clicks with smart analytics — so you always know what's working.
                </p>
                <div className="text-xl sm:text-2xl font-bold">
                  <span className="text-primary">Easy.</span>{" "}
                  <span className="text-primary">Affordable.</span>{" "}
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Smarter.</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  asChild
                  variant="cta"
                  size="lg"
                  className="text-base font-semibold shadow-lg hover:shadow-xl transition-shadow w-full sm:w-auto"
                >
                  <Link to="/signup">
                    Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-base font-semibold backdrop-blur-sm w-full sm:w-auto"
                  onClick={() => setIsDemoVideoOpen(true)}
                >
                  <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Watch Demo
                </Button>
              </div>

              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-muted-foreground text-sm">No credit card required · Easy setup, takes only minutes</span>
              </div>
            </div>

            {/* Carousel — top on mobile, right on desktop */}
            <div
              className="relative order-1 lg:order-2 group"
              onMouseEnter={() => handleCarouselHover(true)}
              onMouseLeave={() => handleCarouselHover(false)}
            >
              <div className="relative">

                {/* Slide label — fixed height row above carousel */}
                <div className="flex items-center justify-center gap-2 mb-3 min-h-[28px]">
                  <span className="text-xs sm:text-sm text-muted-foreground font-medium whitespace-nowrap">
                    {currentSlide === 0 ? "Image Enhancement" : "Text Enhancement"}
                  </span>
                  {!isAnimating && !isHovered && (
                    <div className="flex items-center gap-1 text-xs text-blue-600 whitespace-nowrap">
                      <span>Auto-advancing</span>
                      <div className="w-1 h-1 bg-blue-600 rounded-full animate-pulse"></div>
                    </div>
                  )}
                </div>

                {/* ── Slide 0: Image Enhancement ── */}
                {currentSlide === 0 && (
                  <div className="relative aspect-[16/10] rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-gray-900">
                    {isAnimating && <AutoIQOverlay isAnimating label="Image" />}
                    {showAutoAI && isAnimating && <SparkleEffects />}

                    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
                      {/* LEFT: blurred original */}
                      <div className="relative bg-gray-50 dark:bg-gray-800/50">
                        <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-gray-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold z-10">
                          ORIGINAL
                        </div>
                        <img
                          src={heroProperties}
                          alt="Property - Original"
                          className="w-full h-full object-cover blur-md scale-105"
                        />
                      </div>

                      {/* RIGHT: enhanced or loading */}
                      {showEnhancedContent ? (
                        <div className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 dark:from-blue-950 dark:via-purple-950 dark:to-indigo-950">
                          <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold flex items-center gap-1 sm:gap-1.5 z-10">
                            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0l3.09 6.26L22 7.27l-5 4.87 1.18 6.88L12 15.77l-6.18 3.25L7 12.14 2 7.27l6.91-1.01L12 0z" />
                            </svg>
                            <span className="hidden sm:inline">AUTO IQ ENHANCED</span>
                            <span className="sm:hidden">ENHANCED</span>
                          </div>
                          <img
                            src={heroProperties}
                            alt="Property - Enhanced"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center text-gray-400 text-xs sm:text-sm font-medium bg-gray-100 dark:bg-gray-800/30">
                          <div className="text-center px-2">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                            <span className="hidden sm:inline">Enhancing with AUTO IQ…</span>
                            <span className="sm:hidden">Enhancing…</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl pointer-events-none"></div>
                    <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl pointer-events-none"></div>
                  </div>
                )}

                {/* ── Slide 1: Text Enhancement ── */}
                {currentSlide === 1 && (
                  <div className="relative aspect-[16/10] rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-gray-900">
                    {isAnimating && <AutoIQOverlay isAnimating label="Text" />}
                    {showAutoAI && isAnimating && <SparkleEffects />}

                    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
                      {/* LEFT: original text */}
                      <div className="p-3 sm:p-6 lg:p-8 flex flex-col justify-center bg-gray-50 dark:bg-gray-800/50 relative">
                        <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-gray-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold">
                          ORIGINAL
                        </div>
                        <div className="space-y-3 sm:space-y-6 mt-6 sm:mt-8">
                          <div>
                            <p className="text-[9px] sm:text-xs font-semibold text-gray-400 uppercase mb-1 sm:mb-2 tracking-wide">
                              Subject Line
                            </p>
                            <p className="text-xs sm:text-base lg:text-lg font-medium text-gray-600 dark:text-gray-300 leading-relaxed">
                              {originalSubject}
                            </p>
                          </div>
                          <div className="pt-2 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-[9px] sm:text-xs font-semibold text-gray-400 uppercase mb-1 sm:mb-2 tracking-wide">
                              Description
                            </p>
                            <p className="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-4 sm:line-clamp-none">
                              {originalDescription}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* RIGHT: enhanced text or loading */}
                      {showEnhancedContent ? (
                        <div className="p-3 sm:p-6 lg:p-8 flex flex-col justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 dark:from-blue-950 dark:via-purple-950 dark:to-indigo-950 relative">
                          <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold flex items-center gap-1 sm:gap-1.5">
                            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0l3.09 6.26L22 7.27l-5 4.87 1.18 6.88L12 15.77l-6.18 3.25L7 12.14 2 7.27l6.91-1.01L12 0z" />
                            </svg>
                            <span className="hidden sm:inline">AUTO IQ ENHANCED</span>
                            <span className="sm:hidden">ENHANCED</span>
                          </div>
                          <div className="space-y-3 sm:space-y-6 mt-6 sm:mt-8">
                            <div>
                              <p className="text-[9px] sm:text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase mb-1 sm:mb-2 tracking-wide">
                                Subject Line
                              </p>
                              <p className="text-xs sm:text-base lg:text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-relaxed">
                                {enhancedSubject}
                              </p>
                            </div>
                            <div className="pt-2 sm:pt-4 border-t border-blue-200 dark:border-blue-800">
                              <p className="text-[9px] sm:text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase mb-1 sm:mb-2 tracking-wide">
                                Description
                              </p>
                              <p className="text-[10px] sm:text-sm font-medium text-gray-800 dark:text-gray-200 leading-relaxed line-clamp-4 sm:line-clamp-none">
                                {enhancedDescription}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center text-gray-400 text-xs sm:text-sm font-medium bg-gray-100 dark:bg-gray-800/30">
                          <div className="text-center px-2">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                            <span className="hidden sm:inline">Enhancing with AUTO IQ…</span>
                            <span className="sm:hidden">Enhancing…</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="absolute -top-4 -right-4 w-24 h-24 bg-purple-500/20 rounded-full blur-2xl pointer-events-none"></div>
                    <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl pointer-events-none"></div>
                  </div>
                )}

                {/* ── Prev / Next buttons ── */}
                <button
                  onClick={prevSlide}
                  className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-1.5 sm:p-3 rounded-full shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all hover:scale-110 z-40"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6 text-gray-800 dark:text-white" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-1.5 sm:p-3 rounded-full shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all hover:scale-110 z-40"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6 text-gray-800 dark:text-white" />
                </button>

                {/* ── Slide indicators ── */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-40">
                  <button
                    onClick={() => { clearAllTimers(); setCurrentSlide(0); }}
                    className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${currentSlide === 0 ? "bg-blue-600 w-6 sm:w-8" : "bg-gray-400 w-1.5 sm:w-2 hover:bg-gray-500"}`}
                    aria-label="Go to slide 1"
                  />
                  <button
                    onClick={() => { clearAllTimers(); setCurrentSlide(1); }}
                    className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${currentSlide === 1 ? "bg-blue-600 w-6 sm:w-8" : "bg-gray-400 w-1.5 sm:w-2 hover:bg-gray-500"}`}
                    aria-label="Go to slide 2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features Section ── */}
      <section id="features" className="py-16 sm:py-20 bg-secondary/30">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Everything You Need to{" "}
              <span className="text-red-500">Win More Listings</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              MyListIQ streamlines your marketing by combining powerful import services, AI-powered content
              generation and professional templates.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="bg-card shadow-card border-0 transition-smooth hover:shadow-elevated hover:scale-105 hover:-translate-y-2 group cursor-pointer"
              >
                <CardContent className="p-6 text-center shadow-card">
                  <div className="bg-gradient-primary p-3 rounded-xl w-fit ml-2 mr-auto mb-4 transition-smooth group-hover:scale-110">
                    <feature.icon className="h-6 w-6 text-white transition-smooth group-hover:rotate-6" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 transition-smooth text-left ml-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground transition-smooth group-hover:text-foreground text-left ml-2 text-sm sm:text-base">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Flexible Subscription Banner ── */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#184ccc] rounded-3xl p-6 sm:p-8 lg:p-12 text-center text-white">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Roll with You</h2>
            <p className="text-base sm:text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Real estate moves in seasons. Your plan does too with built-in 1 or 2 month rollover each year,
              based on your subscription.
            </p>
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 sm:px-6 py-3 rounded-full backdrop-blur-sm">
              <Zap className="h-5 w-5 text-white" />
              <span className="text-white font-medium text-sm sm:text-base">No long-term contracts</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing Section ── */}
      <section id="pricing" className="py-16 sm:py-20 bg-background">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              <span className="text-foreground">Simple, Transparent </span>
              <span className="text-red-500">Pricing</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your business. All plans include our core features with no hidden fees.
            </p>
          </div>

          {/* Billing cycle toggle */}
          <div className="mb-10 sm:mb-12 flex flex-col items-center gap-3">
            <div className="relative inline-flex items-center rounded-full bg-muted p-1 shadow-sm">
              <div
                className={`absolute top-1 h-[calc(100%-8px)] w-[calc(50%-4px)] bg-background rounded-full shadow-md transition-all duration-300 ease-in-out ${billingCycle === "monthly" ? "left-1" : "left-[calc(50%+2px)]"}`}
              />
              <button
                type="button"
                onClick={() => setBillingCycle("monthly")}
                className={`relative z-10 px-5 sm:px-6 py-2 text-sm font-medium rounded-full transition-colors duration-300 ease-in-out focus:outline-none ${billingCycle === "monthly" ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"}`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle("annual")}
                className={`relative z-10 px-5 sm:px-6 py-2 text-sm font-medium rounded-full transition-colors duration-300 ease-in-out focus:outline-none ${billingCycle === "annual" ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"}`}
              >
                Annual
              </button>
            </div>
            <div className={`mt-2 flex justify-center transition-all duration-300 ease-in-out ${billingCycle === "annual" ? "opacity-100 scale-100" : "opacity-70 scale-95"}`}>
              <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-gradient-to-r from-green-50 via-emerald-50 to-green-100 px-4 py-1.5 shadow-sm">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-[10px] font-bold text-white">%</span>
                <span className="text-xs sm:text-sm font-medium text-green-900">Go Annual. Save 10% More</span>
              </div>
            </div>
          </div>

          {plansLoading ? (
            <div className="text-center py-20 text-xl text-muted-foreground">Loading plans...</div>
          ) : (
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${billingCycle === "annual" ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-6 sm:gap-8`}>
              {/* One-time plans (monthly only) */}
              {billingCycle === "monthly" &&
                safePricingPlans
                  .filter((p) => isOneTimeName(p.name))
                  .sort((a, b) => getPlanSortIndex(a.name) - getPlanSortIndex(b.name))
                  .map((plan) => (
                    <PricingCardHome
                      key={plan.id}
                      plan={plan}
                      isOneTime
                      billingCycle={billingCycle}
                      allPlans={safePricingPlans}
                      hasUsedFreeTrial={effectiveHasUsedFreeTrial}
                    />
                  ))}

              {/* Monthly / Annual grouped plans */}
              {(() => {
  const grouped = safePricingPlans.reduce((acc: any, plan: any) => {
    const base = displayBaseName(plan.name);
    if (!acc[base]) {
      acc[base] = { 
        monthly: null, 
        annual: null, 
        monthlyFeatures: [], 
        annualFeatures: [] 
      };
    }

    if (plan.billing_cycle === "monthly") {
      acc[base].monthly = plan;
      acc[base].monthlyFeatures = Array.isArray(plan.features) ? plan.features : [];
    } else if (plan.billing_cycle === "annual") {
      acc[base].annual = plan;
      acc[base].annualFeatures = Array.isArray(plan.features) ? plan.features : [];
    }

    return acc;
  }, {});

  return Object.values(grouped)
    .filter((g: any) => !isOneTimeName(g.monthly?.name) && !isOneTimeName(g.annual?.name))
    .sort((a: any, b: any) => {
      const planA = a.monthly || a.annual;
      const planB = b.monthly || b.annual;
      return getPlanSortIndex(planA.name) - getPlanSortIndex(planB.name);
    })
    .map((group: any) => {
      const plan = billingCycle === "monthly" ? group.monthly : group.annual;
      if (!plan) return null;

      const featuresToShow = billingCycle === "monthly" 
        ? (group.monthlyFeatures || group.monthly?.features || [])
        : (group.annualFeatures || group.annual?.features || []);

      return (
        <PricingCardHome
          key={plan.id}
          plan={plan}
          features={featuresToShow}
          showAnnualSavings={billingCycle === "annual"}
          billingCycle={billingCycle}
          allPlans={safePricingPlans}
          hasUsedFreeTrial={effectiveHasUsedFreeTrial}
        />
      );
    });
})()}
            </div>
          )}
        </div>
      </section>

      {/* ── Trial Dialog ── */}
      <Dialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
        <DialogContent className="sm:max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>7-Day Free Trial</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              This plan includes a 7-day free trial with up to 2 free launches. After 7 days, your remaining
              plan credits will be added to your account.
            </p>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setTrialDialogOpen(false)} disabled={isTrialDialogLoading}>
              Cancel
            </Button>
            <Button variant="cta" onClick={confirmTrialDialogPurchase} disabled={isTrialDialogLoading}>
              {isTrialDialogLoading ? "Continuing..." : "Continue to Checkout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Demo Video Dialog ── */}
      <Dialog open={isDemoVideoOpen} onOpenChange={setIsDemoVideoOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-white rounded-2xl shadow-2xl mx-4 sm:mx-auto">
          <DialogHeader className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 text-center bg-gradient-to-b from-gray-50 to-white">
            <DialogTitle className="text-2xl sm:text-3xl font-bold text-black">
              See MyListIQ in Action
            </DialogTitle>
            <p className="mt-2 text-base sm:text-lg text-gray-600">
              Watch how real estate agents create stunning marketing in minutes
            </p>
          </DialogHeader>
          <div className="relative w-full bg-black">
            <video src="/videos/MyListIQ_Demo_V2.mp4" controls autoPlay className="w-full aspect-video">
              <source src="/videos/MyListIQ_Demo_V2.mp4" />
              Your browser does not support video playback.
            </video>
          </div>
          <div className="px-6 sm:px-8 py-4 sm:py-6 text-center bg-gray-50">
            <p className="text-sm text-gray-600">
              Ready to grow your business?{" "}
              <span className="font-bold text-cta">Start your free trial →</span>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <CallToActionFooter />

      <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-[9999] flex flex-col items-end gap-3 sm:gap-4">
        {/* <CallingBot />
        <Chatbot /> */}
        <BotLauncher />
      </div>
    </div>
  );
};

export default Home;